import { TagGroupCreateInput, TagGroupUpdateInput } from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createTagGroup,
  deleteTagGroup,
  getTagGroup,
  listTagGroups,
  reorderTagGroups,
  updateTagGroup
} from '../api/tag-group-api';

export const tagGroupKeys = {
  all: ['tag-group'] as const,
  lists: () => [...tagGroupKeys.all, 'list'] as const,
  list: () => [...tagGroupKeys.lists()] as const,
  details: () => [...tagGroupKeys.all, 'detail'] as const,
  detail: (id: number) => [...tagGroupKeys.details(), id] as const,
};

export const useTagGroups = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: tagGroupKeys.list(),
    queryFn: async () => {
      const groups = await listTagGroups();
      groups.forEach((group) => {
        queryClient.setQueryData(tagGroupKeys.detail(group.id), group);
      });
      return groups;
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useTagGroupDetail = (id: number) => {
  return useQuery({
    queryKey: tagGroupKeys.detail(id),
    queryFn: () => getTagGroup(id),
    enabled: id > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useTagGroupCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TagGroupCreateInput) => createTagGroup(data),
    onSuccess: (created) => {
      queryClient.setQueryData(tagGroupKeys.detail(created.id), created);
      queryClient.invalidateQueries({ queryKey: tagGroupKeys.lists() });
    },
  });
};

export const useTagGroupUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: TagGroupUpdateInput }) => updateTagGroup(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(tagGroupKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: tagGroupKeys.lists() });
    },
  });
};

export const useTagGroupDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteTagGroup(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: tagGroupKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tagGroupKeys.lists() });
    },
  });
};

export const useTagGroupReorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupIds: number[]) => reorderTagGroups({ groupIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tagGroupKeys.lists() });
    },
  });
};
